import { AxiosError, AxiosResponse } from 'axios';
import qs from 'qs-stringify';

import { RESPONSE_CODES } from '../constants/responseCodes.constants';
import { PaginationFactory, PaginatorFactory } from '../factory';
import { api } from '../http';
import { store } from '../store';
import { DefaultRes, DeviceTypes } from '../types';

export class DeviceService {
    public static readonly dispatch = store.dispatch.device;
    public static readonly state = store.getState();

    public static async fetchMany(
        dto: PaginatorFactory<{}>,
        callback?: (isValid?: boolean, message?: string) => void,
        ignoreState = false,
        assigned = true
    ): Promise<PaginationFactory<DeviceTypes.Model> | void> {
        const {
            dispatch: { toggleLoading, setPagination },
        } = DeviceService;

        if (!ignoreState) {
            toggleLoading('fetchMany', true);
        }

        try {
            const qsfy = qs({
                perPage: dto.perPage,
                page: dto?.page,
                assigned: String(assigned),
                filter: {
                    ...dto?.filter,
                },
                sort: {
                    ...dto?.sort,
                }
            });

            const {
                data: {
                    result: { items, page, perPage, total },
                    isValid,
                    message,
                },
            } = await api.get<
                DefaultRes<PaginationFactory<DeviceTypes.Model>>,
                AxiosResponse<DefaultRes<PaginationFactory<DeviceTypes.Model>>>
            >(`/devices/getMany?${qsfy}`); 

            if (!ignoreState) {
                toggleLoading('fetchMany', false);

                if (isValid) {
                    setPagination({ items, page, perPage, total });
                }
            }
            
            callback && callback(isValid, RESPONSE_CODES[message]);

            return { items, page, perPage, total };
        } catch (error: any) {
            toggleLoading('fetchMany', false);
            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<DeviceTypes.Model>>
                ).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            }
            callback && callback(false, String(error));
        }
    }

    public static async destroy(
        id: string,
        callback?: (isValid?: boolean, message?: string) => void
    ) {
        const {
            dispatch: { toggleLoading, destroyDevice },
        } = DeviceService;

        try {
            toggleLoading('delete', true);

            const {
                data: { isValid, message },
            } = await api.delete<
                DefaultRes<DeviceTypes.Model>,
                AxiosResponse<DefaultRes<DeviceTypes.Model>>
            >(`/devices/destroy/${id}`);

            toggleLoading('delete', false);

            destroyDevice(id);

            return callback && callback(isValid, RESPONSE_CODES[message]);
        } catch (error: any) {
            toggleLoading('delete', false);
            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<DeviceTypes.Model>>
                ).response?.data?.message;

                return callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            }
            return callback && callback(false, String(error));
        }
    }

    public static async unassign(
        id: string,
        callback?: (isValid?: boolean, message?: string) => void
    ) {
        const {
            dispatch: { toggleLoading },
        } = DeviceService;

        try {
            toggleLoading('unassign', true);

            const {
                data: { isValid, message },
            } = await api.put<
                DefaultRes<DeviceTypes.Model>,
                AxiosResponse<DefaultRes<DeviceTypes.Model>>
            >(`/devices/${id}/unassign`);

            toggleLoading('unassign', false);

            return callback && callback(isValid, RESPONSE_CODES[message]);
        } catch (error: any) {
            toggleLoading('unassign', false);
            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<DeviceTypes.Model>>
                ).response?.data?.message;

                return callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            }
            return callback && callback(false, String(error));
        }
    }

    public static async updateAssetTag(
        deviceId: string,
        assetTag: string | null,
        callback?: (isValid?: boolean, message?: string) => void,
    ): Promise<void> {
        try {
            const {
                data: { isValid, message },
            } = await api.put<
                DefaultRes<DeviceTypes.Model>,
                AxiosResponse<DefaultRes<DeviceTypes.Model>>
            >(`/devices/${deviceId}/assetTag`, { assetTag });

            return callback && callback(isValid, RESPONSE_CODES[message]);
        } catch (error: any) {
            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<DeviceTypes.Model>>
                ).response?.data?.message;

                return callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            }
            return callback && callback(false, String(error));
        }
    }

    public static async updateDeviceNotes(
        deviceId: string,
        notes: string | null,
        callback?: (isValid?: boolean, message?: string) => void,
    ): Promise<void> {
        try {
            const {
                data: { isValid, message },
            } = await api.put<
                DefaultRes<DeviceTypes.Model>,
                AxiosResponse<DefaultRes<DeviceTypes.Model>>
            >(`/devices/${deviceId}/notes`, { notes });

            return callback && callback(isValid, RESPONSE_CODES[message]);
        } catch (error: any) {
            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<DeviceTypes.Model>>
                ).response?.data?.message;

                return callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            }
            return callback && callback(false, String(error));
        }
    }

    public static async updateDeviceReturnStatus(
        deviceId: string,
        returnStatus: string | null,
        callback?: (isValid?: boolean, message?: string) => void,
    ): Promise<void> {
        try {
            const {
                data: { isValid, message },
            } = await api.put<
                DefaultRes<DeviceTypes.Model>,
                AxiosResponse<DefaultRes<DeviceTypes.Model>>
            >(`/devices/${deviceId}/returnStatus`, { returnStatus });

            return callback && callback(isValid, RESPONSE_CODES[message]);
        } catch (error: any) {
            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<DeviceTypes.Model>>
                ).response?.data?.message;

                return callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            }
            return callback && callback(false, String(error));
        }
    }
}
